// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Slots Floor
// Grid of all available slot machines
// ================================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCasino } from '../context/CasinoShell';

const SLOT_GAMES = [
  {
    id: 'classic-forge',
    title: 'Classic Forge',
    icon: '🔥',
    description: 'Classic 3-reel slot with Forge-themed symbols. Simple, fast, and provably fair.',
    reels: 3,
    paylines: 5,
    minBet: 0.50,
    maxBet: 50.00,
    rtp: '95.2%',
    volatility: 'Medium',
    maxWin: '500x',
    features: ['Provably Fair', 'Auto-Play'],
    color: '#DC2626',
    status: 'live',
  },
  {
    id: 'forge-rush',
    title: 'Forge Rush',
    icon: '⚡',
    description: '5-reel video slot with bonus rounds, wilds, and scatter-triggered free spins.',
    reels: 5,
    paylines: 20,
    minBet: 0.20,
    maxBet: 100.00,
    rtp: '96.1%',
    volatility: 'High',
    maxWin: '2,500x',
    features: ['Wilds', 'Scatters', 'Free Spins', 'Bonus Round'],
    color: '#FFD700',
    status: 'live',
  },
  {
    id: 'golden-hammer',
    title: 'Golden Hammer',
    icon: '🔨',
    description: 'Progressive jackpot slot. Every spin feeds the jackpot pool. Hit 5 Forge symbols to win it all.',
    reels: 5,
    paylines: 20,
    minBet: 1.00,
    maxBet: 200.00,
    rtp: '94.0%',
    volatility: 'Very High',
    maxWin: 'JACKPOT',
    features: ['Progressive Jackpot', 'Wilds', 'Scatters', 'Multipliers'],
    color: '#00FF88',
    status: 'live',
  },
];

export default function SlotsFloor() {
  const navigate = useNavigate();
  const { forgeBalance } = useCasino();
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? SLOT_GAMES : SLOT_GAMES.filter(g => {
    if (filter === 'classic') return g.reels === 3;
    if (filter === 'video') return g.reels === 5;
    if (filter === 'jackpot') return g.features.includes('Progressive Jackpot');
    return true;
  });

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🎰 SLOTS FLOOR</h1>
          <p style={styles.subtitle}>{SLOT_GAMES.length} provably fair slot machines</p>
        </div>
        <div style={styles.balanceBadge}>
          <span style={styles.balanceLabel}>BALANCE</span>
          <span style={styles.balanceValue}>${forgeBalance.toFixed(2)}</span>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        {['all', 'classic', 'video', 'jackpot'].map(f => (
          <button
            key={f}
            style={{
              ...styles.filterBtn,
              background: filter === f ? '#DC2626' : '#1a1a1a',
              color: filter === f ? '#fff' : '#888',
            }}
            onClick={() => setFilter(f)}
          >
            {f.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Game List */}
      <div style={styles.gameList}>
        {filtered.map(game => (
          <div key={game.id} style={styles.gameCard}>
            <div style={styles.cardHeader}>
              <span style={styles.gameIcon}>{game.icon}</span>
              <div>
                <h3 style={{ ...styles.gameTitle, color: game.color }}>{game.title}</h3>
                <p style={styles.gameDesc}>{game.description}</p>
              </div>
            </div>

            <div style={styles.specGrid}>
              <div style={styles.spec}>
                <span style={styles.specLabel}>REELS</span>
                <span style={styles.specValue}>{game.reels}</span>
              </div>
              <div style={styles.spec}>
                <span style={styles.specLabel}>PAYLINES</span>
                <span style={styles.specValue}>{game.paylines}</span>
              </div>
              <div style={styles.spec}>
                <span style={styles.specLabel}>RTP</span>
                <span style={styles.specValue}>{game.rtp}</span>
              </div>
              <div style={styles.spec}>
                <span style={styles.specLabel}>MAX WIN</span>
                <span style={{ ...styles.specValue, color: '#FFD700' }}>{game.maxWin}</span>
              </div>
              <div style={styles.spec}>
                <span style={styles.specLabel}>VOLATILITY</span>
                <span style={styles.specValue}>{game.volatility}</span>
              </div>
              <div style={styles.spec}>
                <span style={styles.specLabel}>BET RANGE</span>
                <span style={styles.specValue}>${game.minBet}-${game.maxBet}</span>
              </div>
            </div>

            <div style={styles.features}>
              {game.features.map(f => (
                <span key={f} style={styles.featureTag}>{f}</span>
              ))}
            </div>

            <button
              style={styles.playBtn}
              onClick={() => navigate(`/slots/${game.id}`)}
            >
              🎰 PLAY {game.title.toUpperCase()}
            </button>
          </div>
        ))}
      </div>

      {/* Fair play footer */}
      <div style={styles.footer}>
        <p style={styles.footerText}>
          All slot machines use SHA-256 provably fair technology. Every spin is cryptographically verified.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '16px',
    maxWidth: 700,
    margin: '0 auto',
    minHeight: '100vh',
    background: '#0a0a0a',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: 900,
    color: '#fff',
    letterSpacing: 2,
    margin: 0,
  },
  subtitle: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },
  balanceBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: '8px 14px',
    background: '#111',
    borderRadius: 8,
    border: '1px solid #222',
  },
  balanceLabel: { fontSize: 8, color: '#555', fontWeight: 700, letterSpacing: 1 },
  balanceValue: { fontSize: 18, fontWeight: 900, color: '#FFD700', fontFamily: "'Courier New', monospace" },
  filters: {
    display: 'flex',
    gap: 6,
    marginBottom: 16,
  },
  filterBtn: {
    padding: '6px 14px',
    borderRadius: 6,
    border: '1px solid #333',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    cursor: 'pointer',
  },
  gameList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  gameCard: {
    padding: '16px',
    background: '#111',
    borderRadius: 12,
    border: '1px solid #222',
  },
  cardHeader: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  gameIcon: { fontSize: 36 },
  gameTitle: {
    fontSize: 16,
    fontWeight: 900,
    letterSpacing: 1,
    margin: 0,
  },
  gameDesc: {
    fontSize: 11,
    color: '#888',
    lineHeight: 1.5,
    marginTop: 4,
  },
  specGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginBottom: 12,
  },
  spec: {
    padding: '6px 8px',
    background: '#0a0a0a',
    borderRadius: 6,
    border: '1px solid #1a1a1a',
    textAlign: 'center',
  },
  specLabel: { display: 'block', fontSize: 7, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 2 },
  specValue: { fontSize: 12, fontWeight: 900, color: '#fff' },
  features: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },
  featureTag: {
    padding: '3px 8px',
    borderRadius: 4,
    background: '#DC262622',
    border: '1px solid #DC262633',
    fontSize: 9,
    color: '#DC2626',
    fontWeight: 700,
  },
  playBtn: {
    width: '100%',
    padding: '12px',
    borderRadius: 8,
    border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #991B1B 100%)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 2,
    cursor: 'pointer',
    boxShadow: '0 0 12px #DC262633',
  },
  footer: {
    textAlign: 'center',
    padding: '20px 0',
    marginTop: 20,
  },
  footerText: {
    fontSize: 10,
    color: '#444',
    lineHeight: 1.5,
  },
};
